import { useEffect, useState } from 'react';

import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CheckOutline, useToast } from '@aslanpay/portal-common-lib';

import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { DigitalVouchersConfigModalProps } from './DigitalVouchersConfigModal.types';

function DigitalVouchersConfigModal({
  isOpen,
  onClose,
  companyId,
  initialPointsPerSpend = undefined,
}: DigitalVouchersConfigModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const initialValue = initialPointsPerSpend != null ? String(initialPointsPerSpend) : '';

  const [pointsPerSpend, setPointsPerSpend] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPointsPerSpend(initialValue);
    }
  }, [isOpen, initialValue]);

  const isDirty = pointsPerSpend !== initialValue;
  const parsedPoints = Number(pointsPerSpend);
  const isValid =
    pointsPerSpend.trim() !== '' && Number.isFinite(parsedPoints) && parsedPoints >= 0;

  const handleClose = () => {
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscard = () => {
    setIsDiscardOpen(false);
    setPointsPerSpend(initialValue);
    onClose();
  };

  const handleSave = async () => {
    if (!isDirty || !isValid) return;

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyId,
        digitalVouchersConfig: { pointsPerDigitalVoucherSpend: parsedPoints },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The digital vouchers configuration was successfully saved.',
      });

      queryClient.invalidateQueries({ queryKey: ['fetchCompanyConfiguration', companyId] });
      onClose();
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the digital vouchers configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Digital Vouchers settings</ModalHeader>
          <ModalCloseButton data-testid="digital-vouchers-modal-close" />
          <ModalBody>
            <VStack align="stretch" spacing="20px">
              <FormControl>
                <FormLabel>Points per digital voucher spend</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={pointsPerSpend}
                  onChange={(event) => setPointsPerSpend(event.target.value)}
                  data-testid="points-per-voucher-input"
                />
                <FormHelperText>
                  Base value used to calculate how many points employees earn on digital voucher
                  purchases. The points actually earned vary per voucher based on its commission
                  rates.
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap="10px">
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              leftIcon={<CheckOutline boxSize="20px" />}
              size="sm"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!isDirty || !isValid}
              data-testid="save-digital-vouchers-button"
            >
              Save changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDiscardOpen} onClose={() => setIsDiscardOpen(false)} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Discard changes?</ModalHeader>
          <ModalBody>
            <Text textStyle="smallRegular" color="brand.darkGray">
              You have unsaved changes. Are you sure you want to discard them?
            </Text>
          </ModalBody>
          <ModalFooter gap="10px">
            <Button variant="outline" size="sm" onClick={() => setIsDiscardOpen(false)}>
              Keep editing
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDiscard}
              data-testid="discard-digital-vouchers-button"
            >
              Discard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export { DigitalVouchersConfigModal };
